import {
  Dialog,
  DialogContent,
  DialogTitle,
  styled,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { CloseCircle } from 'assets';
import PaymentStripe from 'components/common/Payments/components/PaymentStripe';
import { STORAGE_KEY } from 'constants/global';
import { PAYMENT_METHOD, PAYMENT_STATUS, PAYMENT_TYPE } from 'models/payments';
import { useLocation, useSearchParams } from 'react-router-dom';
import { APP_ROUTES } from 'routers/routes';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setGlobalLoading } from 'store/reducers/global';
import { updateStatusPayNow } from 'store/reducers/payments';
import { theme } from 'theme';
import { getLocalStorage, sleep } from 'utils';
import PaymentFailed from '../PaymentFailed';
import PaymentPayNow from '../PaymentPayNow';

const PaymentModalWrapper = styled(Dialog)(() => ({
  '& .MuiDialogTitle-root': {
    padding: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '& .title-modal': {
      fontSize: '24px',
      fontWeight: 700,
      lineHeight: '36px',
      paddingTop: 1,
    },
  },
  '& .MuiDialogContent-root': {
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0px !important',
    '& .countdown': {
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '24px',
      textAlign: 'center',
    },
  },
}));

interface PaymentModalProps {
  onClose: () => void;
  methodType: PAYMENT_METHOD;
  valueQrCode: string;
  qrCodeKey?: string;
  paymentType: PAYMENT_TYPE;
  retryPaymentCallback: () => void;
}

export const PaymentModal = ({
  methodType,
  onClose,
  valueQrCode,
  paymentType,
  qrCodeKey,
  retryPaymentCallback,
  ...rest
}: PaymentModalProps) => {
  const dispatch = useAppDispatch();
  const pathname = useLocation().pathname;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isMethodPayNow = methodType === PAYMENT_METHOD.PAY_NOW;
  const { statusPayNow } = useAppSelector((state) => state.payment);

  const [searchParams] = useSearchParams();
  const isJobTrial = searchParams.get('isTrial') === 'true';
  const isJobExtend = searchParams.get('isExtend') === 'true';

  const _onCloseModal = async () => {
    dispatch(setGlobalLoading(true));
    await sleep(500);
    onClose();
    dispatch(updateStatusPayNow(PAYMENT_STATUS.NULL));
    dispatch(setGlobalLoading(false));
  };

  const getQrCode = () => {
    switch (true) {
      case pathname === APP_ROUTES.CREATE_REQUEST.path:
        return getLocalStorage(STORAGE_KEY.QR_CODE_SEARCH_FEE);
      case pathname === APP_ROUTES.JOB.PAYMENT.to && isJobTrial:
        return getLocalStorage(STORAGE_KEY.QR_CODE_TRIAL_JOB);
      case pathname === APP_ROUTES.JOB.PAYMENT.to && isJobExtend:
        return getLocalStorage(STORAGE_KEY.QR_CODE_TOP_UP);
      default:
        return valueQrCode;
    }
  };

  return (
    <PaymentModalWrapper
      open
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          padding: 4,
          width: isMethodPayNow ? '375px' : '400px',
          margin: isMobile ? 2 : 0,
          height: 'auto',
        },
      }}
      onClose={!isMethodPayNow && _onCloseModal}
      {...rest}
    >
      <DialogTitle padding={0}>
        <Typography className="title-modal">
          {isMethodPayNow
            ? statusPayNow === PAYMENT_STATUS.FAILED
              ? ''
              : 'PayNow'
            : 'Add Debit/Credit Card'}
        </Typography>
        {statusPayNow !== PAYMENT_STATUS.FAILED && (
          <CloseCircle
            onClick={_onCloseModal}
            sx={{ color: theme.palette.grey1.dark }}
            cursor={'pointer'}
          />
        )}
      </DialogTitle>
      <DialogContent>
        {isMethodPayNow ? (
          statusPayNow === PAYMENT_STATUS.FAILED ? (
            <PaymentFailed onClose={_onCloseModal} />
          ) : (
            <PaymentPayNow
              valueQrCode={getQrCode()}
              qrCodeKey={qrCodeKey}
              retryPaymentCallback={retryPaymentCallback}
            />
          )
        ) : (
          <PaymentStripe />
        )}
      </DialogContent>
    </PaymentModalWrapper>
  );
};

export default PaymentModal;
